
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import mixins from 'vue-typed-mixins';

import { formatNumber } from '@/i18n';

import Pagination from '@/store/models/pagination';
import CheckboxControl from '@/components/controls/checkbox.vue';

import tableViewMixin from '@/components/mixins/table-view';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import TableListSkeleton from '@/components/skeletons/table-list.vue';
import Dropdown, { DropdownItems } from '@/components/controls/dropdown.vue';
import PaginationControl from '@/components/controls/pagination.vue';
import EnhancementsFilter from '@/components/forms/enhancements-filter.vue';
import { TreatmentActions } from '@/store/modules/treatments/types';
import TreatmentEnhancement, { TreatmentEnhancementStatus } from '@/store/modules/treatments/enhancement';

export default mixins(tableViewMixin).extend({
  name: 'EnhancementsView',
  components: {
    NotificationBanner,
    TableListSkeleton,
    Dropdown,
    PaginationControl,
    EnhancementsFilter,
    CheckboxControl,
  },
  data() {
    return {
      selectedItems: [],
      TreatmentEnhancementStatus,
    };
  },
  computed: {
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin']),
    ...mapState('treatments', {
      enhancements: 'enhancementList',
      pagination: 'enhancementListPagination',
    }),
    ...mapGetters('treatments', {
      isLoading: 'enhancementListIsLoading',
      isLoaded: 'enhancementListIsLoaded',
      isEmpty: 'enhancementListIsEmpty',
    }),
    areAllItemsSelected(): boolean {
      return this.selectedItems.length === this.enhancements.length;
    },
  },
  methods: {
    formatNumber,
    ...mapActions('treatments', {
      restoreAction: TreatmentActions.RESTORE_ENHANCEMENT,
      removeAction: TreatmentActions.DELETE_ENHANCEMENT,
    }),
    dropdownItems(enhancement: TreatmentEnhancement) {
      const items: DropdownItems = {
        edit: {
          label: this.$t('Edit'),
          icon: 'pencil',
        },
      };

      if (enhancement.status === TreatmentEnhancementStatus.Inactive) {
        items.restore = {
          label: this.$t('Restore'),
          icon: 'restore',
        };
      } else {
        items.remove = {
          label: this.$t('Delete'),
          hasTopBorder: true,
          icon: 'trash',
        };
      }

      return items;
    },
    handleClick(enhancement: TreatmentEnhancement, action: string) {
      if ('edit' === action) {
        this.$router.push({
          name: 'edit-enhancement',
          params: { id: String(enhancement.id) },
        });
      }

      if ('restore' === action) {
        const answer = window.confirm(this.$t('Do you really want to restore this enhancement?'));

        if (answer) {
          this.restoreAction({ id: enhancement.id });
        }
      }

      if ('remove' === action) {
        const answer = window.confirm(this.$t('Do you really want to delete this enhancement?'));

        if (answer) {
          this.removeAction({ id: enhancement.id });
        }
      }
    },
    isSelected(enhancement: TreatmentEnhancement): boolean {
      return this.selectedItems.indexOf(enhancement.id) !== -1;
    },
    selectItem(enhancement: TreatmentEnhancement) {
      if (this.isSelected(enhancement)) {
        this.selectedItems = this.selectedItems.filter((item) => item !== enhancement.id);
        return;
      }

      this.selectedItems.push(enhancement.id);
    },
    selectAll() {
      if (this.areAllItemsSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.enhancements.map((enhancement: TreatmentEnhancement) => enhancement.id);
      }
    },
  },
});
